import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import React, { useContext, useEffect, useState } from 'react';
import Scatter from '../scatterPlots/Scatter';
import Panel from '../Panel';
import FrontierTooltip from '../tooltips/FrontierTooltip';
import {
    getFrontierActiveDotStyle,
    getFrontierInactiveDotStyle
} from '../../functions/dataFormatting';
import { useTooltip } from '../../hooks/tooltip';
import { useDesktopChartEventHandlers } from '../../hooks/eventHandlers';
import { STATUS } from '../../functions/visibility';
import DesktopRankingTable from '../tables/DesktopRankingTable';
import { FilterStatesContext } from '../contexts/filterStates';
import { setComparisonFilters } from '../../functions/filtering';

/**
 * Represents the Section showing the behavioural alpha frontier graph and ranking table
 * @component
 * @param data {Array<Object>} the data to display in both visualisations.
 * @param {Function} handleMoreDetailsClick - how to handle the tooltip More Details option
 * @param isBasic - true if being rendered for EBAB Basic
 * @returns {React.ReactElement} - rendered tooltip component
 */
const FrontierSection = ({ data, handleMoreDetailsClick, isBasic }) => {
    const theme = useTheme();
    const { tooltipState, setTooltipState, clearTooltip } = useTooltip();
    const [selectedFrontierDot, setSelectedFrontierDot] = useState(null);

    const { peerFilterStates, peerFilterDispatch, setSearchState } =
        useContext(FilterStatesContext);

    const { onMouseOverDot, onMouseOutDot, onClickDot } =
        useDesktopChartEventHandlers(
            { tooltipState, setTooltipState, clearTooltip },
            {
                getActiveDotStyle: getFrontierActiveDotStyle,
                getInactiveDotStyle: getFrontierInactiveDotStyle
            },
            selectedFrontierDot,
            setSelectedFrontierDot
        );

    const [externalDotMutations, setExternalDotMutations] = useState(null);

    function handleVsPeerGroupClick() {
        clearTooltip();
        setSearchState(null);
        setComparisonFilters(tooltipState.selectedDot, peerFilterDispatch);
    }

    /* Set all chart dot styles at once, either with default or faded style depending on whether dot is currently selected */
    function setAllDotMutations() {
        setExternalDotMutations([
            {
                target: 'data',
                eventKey: 'all',
                mutation: (props) =>
                    getFrontierInactiveDotStyle(
                        props,
                        theme,
                        selectedFrontierDot?.isClicked
                    ),
                callback: () => setExternalDotMutations(null)
            }
        ]);
    }

    /* Set all chart dot styles whenever selected dot changes */
    useEffect(() => {
        setAllDotMutations();
    }, [selectedFrontierDot]);

    /**
     * Set all chart dot styles whenever there is a change to the filter states.
     * Styling performed with a slight delay to allow filtering and chart rendering to finish first.
     * This is to prevent a bug where chart dots were still faded after resetting peer filters.
     */
    useEffect(() => {
        setTimeout(() => {
            setAllDotMutations();
        }, 100);
    }, [peerFilterStates]);

    /* Creates event listener to allow clicking away from selected dot by clicking elsewhere, after tooltip is closed */
    useEffect(() => {
        const handleClickAway = (event) => {
            !event.target.closest('.MuiTableCell-root') &&
                setSelectedFrontierDot(null);
        };

        !tooltipState.selected &&
            document.addEventListener('mousedown', handleClickAway, {
                once: true
            });

        return () => {
            document.removeEventListener('mousedown', handleClickAway);
        };
    }, [tooltipState.selected]);

    return (
        <>
            <Panel sx={{ paddingBottom: 2 }}>
                <Grid container spacing={{ sm: 1, md: 2, lg: 3 }}>
                    <Grid item xs={6} xl={6} id='appPageScatterPlot'>
                        <Scatter
                            data={data}
                            dataTestId='ebabScatterPlot'
                            dataAnalyticsId='ebab-chart'
                            onMouseOver={onMouseOverDot}
                            onMouseOut={onMouseOutDot}
                            onClick={onClickDot}
                            externalDotMutations={externalDotMutations}
                            size={
                                theme.sizing.scatterPlot.frontierPoints.radius
                                    .initial
                            }
                            style={{
                                data: {
                                    strokeWidth:
                                        theme.sizing.scatterPlot.frontierPoints
                                            .stroke.initial,
                                    stroke: theme.palette.scatterPlot
                                        .frontierPoints.stroke.initial,
                                    fill: ({ datum }) => datum.dotColor
                                },
                                labels: {
                                    fontFamily: theme.typography.fontFamily,
                                    fontSize: 10 // can't be rem
                                }
                            }}
                            selectedDot={selectedFrontierDot}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        sx={{
                            paddingRight: theme.spacing(2),
                            paddingTop: theme.spacing(3)
                        }}
                        id='appPageRankingTable'
                    >
                        <DesktopRankingTable
                            filteredData={data}
                            selectedPortfolio={selectedFrontierDot}
                            setSelectedPortfolio={setSelectedFrontierDot}
                            onMoreDetailsClick={handleMoreDetailsClick}
                        />
                    </Grid>
                </Grid>
            </Panel>
            {(tooltipState.hovered || tooltipState.selected) && (
                <FrontierTooltip
                    position={{
                        x: tooltipState.posX,
                        y: tooltipState.posY
                    }}
                    dotData={tooltipState.selectedDot}
                    onMoreDetailsClick={() =>
                        handleMoreDetailsClick(tooltipState.selectedDot)
                    }
                    isMoreDetailsDisabled={
                        !isBasic &&
                        tooltipState?.selectedDot.status < STATUS.open
                    }
                    handleClose={(event) => {
                        clearTooltip();
                        !event.target.closest('.MuiTableCell-root') &&
                            setSelectedFrontierDot(null);
                    }}
                    onVsPeerGroupClick={handleVsPeerGroupClick}
                    isSticky={tooltipState.selected}
                />
            )}
        </>
    );
};

FrontierSection.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    handleMoreDetailsClick: PropTypes.func,
    isBasic: PropTypes.bool
};

export default FrontierSection;
